/**
 * MetadataEditableField - Inline editable field for metadata
 *
 * Features:
 * - Click to edit inline
 * - Enter to save, Esc to cancel
 * - Visual feedback (loading, success, error)
 * - Validation support
 * - Clean and minimalist design
 */

import { useState, useRef, useEffect } from 'react'
import { Icon } from '@/design-system/atoms/Icon'

interface MetadataEditableFieldProps {
  label: string
  value: string | null
  fieldKey?: string
  onSave: (value: string) => Promise<void>
  placeholder?: string
  validation?: RegExp
  validationMessage?: string
  type?: 'text' | 'date' | 'select'
  options?: string[]
  required?: boolean
}

type FieldState = 'idle' | 'editing' | 'saving' | 'success' | 'error'

export const MetadataEditableField = ({
  label,
  value,
  onSave,
  placeholder = 'Clique para editar',
  validation,
  validationMessage = 'Valor inválido',
  type = 'text',
  options = [],
  required = false,
}: MetadataEditableFieldProps) => {
  const [state, setState] = useState<FieldState>('idle')
  const [editValue, setEditValue] = useState(value || '')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null)

  useEffect(() => {
    if (state === 'editing' && inputRef.current) {
      inputRef.current.focus()
    }
  }, [state])

  useEffect(() => {
    if (state !== 'editing') {
      setEditValue(value || '')
    }
  }, [value, state])

  const handleStartEdit = () => {
    if (state === 'saving') return
    setState('editing')
    setError(null)
  }

  const handleCancel = () => {
    setState('idle')
    setEditValue(value || '')
    setError(null)
  }

  const handleSave = async () => {
    const trimmedValue = editValue.trim()

    if (required && !trimmedValue) {
      setError('Campo obrigatório')
      return
    }

    if (trimmedValue && validation && !validation.test(trimmedValue)) {
      setError(validationMessage)
      return
    }

    setState('saving')
    setError(null)

    try {
      await onSave(trimmedValue)
      setState('success')
      setTimeout(() => setState('idle'), 1500)
    } catch (err) {
      setState('error')
      setError(err instanceof Error ? err.message : 'Erro ao salvar')
      setTimeout(() => setState('editing'), 2000)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const isEmpty = !value
  const isEditing = state === 'editing' || state === 'saving'

  return (
    <div className="group">
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {isEditing ? (
        <div className="space-y-2">
          {type === 'select' ? (
            <select
              ref={inputRef as React.RefObject<HTMLSelectElement>}
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`w-full px-3 py-2 bg-white dark:bg-gray-900 border rounded-lg text-sm font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 transition-all ${
                error
                  ? 'border-red-500 dark:border-red-400'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              <option value="">Selecione...</option>
              {options.map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type={type}
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className={`w-full px-3 py-2 bg-white dark:bg-gray-900 border rounded-lg text-sm font-medium text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 transition-all ${
                error
                  ? 'border-red-500 dark:border-red-400 animate-shake'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
          )}

          {error && (
            <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
              <Icon name="alert-circle" size={14} />
              {error}
            </p>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={state === 'saving'}
              className="flex-1 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {state === 'saving' ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              onClick={handleCancel}
              disabled={state === 'saving'}
              className="flex-1 px-3 py-1.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={handleStartEdit}
          disabled={false}
          className="w-full text-left px-3 py-2 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg transition-all group-hover:border-brand-400 dark:group-hover:border-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center justify-between gap-2">
            <span
              className={`text-sm font-medium truncate ${
                isEmpty
                  ? 'text-gray-400 dark:text-gray-500 italic'
                  : 'text-gray-900 dark:text-white'
              }`}
            >
              {value || '—'}
            </span>

            {state === 'success' && (
              <Icon
                name="check-circle"
                size={16}
                className="text-green-600 dark:text-green-400 animate-scale-in flex-shrink-0"
              />
            )}

            {state === 'error' && (
              <Icon
                name="x-circle"
                size={16}
                className="text-red-600 dark:text-red-400 flex-shrink-0"
              />
            )}

            {state === 'idle' && (
              <Icon
                name="pencil"
                size={16}
                className="text-gray-400 dark:text-gray-600 group-hover:text-brand-500 dark:group-hover:text-brand-400 transition-colors flex-shrink-0"
              />
            )}
          </div>
        </button>
      )}
    </div>
  )
}
